import React, { useEffect, useRef, useState } from 'react';
import { fetchFunctionSuggestions } from '../../utils/banquetApi';

/**
 * The banquet header: who the function is for, how many plates, at what rate,
 * and the bill those two produce.
 *
 * The bill is SHOWN, never typed. It is arithmetic on the two numbers above it,
 * the server recomputes it in SQL, and a CHECK constraint enforces it — so an
 * editable box here would be a fourth opinion about one number, and the only
 * one that could be wrong.
 *
 * Markup reuses the page's own tsr-manual-* classes so this block is visually
 * part of the entry sheet rather than a panel bolted onto it.
 */
const BanquetEntryFields = ({
  functionName,
  platesOrdered,
  ratePerPlate,
  totalBill,
  lastCascade,
  disabled,
  fieldError,          // { field, message } from a rejected submit
  canConfirm,
  isConfirmed,
  onConfirm,
  onFieldChange,       // (field, value)
  onPlatesChange,      // (intOrNull)
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapRef = useRef(null);

  // Recent function names, debounced. A function run monthly should be one tap
  // to re-enter rather than re-typed (and re-typed slightly differently, which
  // is what function_key exists to absorb).
  useEffect(() => {
    const q = String(functionName || '').trim();
    if (q.length < 2) { setSuggestions([]); return undefined; }
    let alive = true;
    const t = setTimeout(async () => {
      const list = await fetchFunctionSuggestions(q);
      if (alive) setSuggestions(list.filter((n) => n.toLowerCase() !== q.toLowerCase()));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [functionName]);

  useEffect(() => {
    if (!showSuggestions) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showSuggestions]);

  const errFor = (field) => (fieldError && fieldError.field === field ? fieldError.message : null);

  const money = (n) => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="bq-fields">
      <div className="bq-fields-title">Banquet Details</div>

      {/* Function name, with recent names */}
      <div className="tsr-manual-form-group" style={{ position: 'relative' }} ref={wrapRef}>
        <label className="tsr-manual-label" htmlFor="bq-function-name">Function name</label>
        <input
          id="bq-function-name"
          type="text"
          className="tsr-manual-input"
          placeholder="e.g. Sharma Reception"
          value={functionName || ''}
          maxLength={120}
          disabled={disabled}
          onChange={(e) => { onFieldChange('functionName', e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
        />
        {errFor('functionName') && <div className="bq-field-error">{errFor('functionName')}</div>}

        {showSuggestions && suggestions.length > 0 && (
          <div className="tsr-manual-suggestions">
            {suggestions.map((name) => (
              <div
                key={name}
                className="tsr-manual-suggestion"
                onClick={() => { onFieldChange('functionName', name); setShowSuggestions(false); }}
              >
                <span className="tsr-manual-suggestion-name">{name}</span>
                <span className="tsr-manual-suggestion-price">recent</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="tsr-manual-inline-fields bq-inline-fields">
        <div className="tsr-manual-form-group">
          <label className="tsr-manual-label" htmlFor="bq-plates">Total plates</label>
          <input
            id="bq-plates"
            type="number"
            className="tsr-manual-input"
            placeholder="0"
            min="1"
            step="1"
            value={platesOrdered ?? ''}
            disabled={disabled}
            onChange={(e) => {
              const raw = e.target.value;
              // Parsed to an integer or to null. Never to 0 — an empty box
              // means "not decided yet", and 0 plates is not a banquet.
              const n = raw === '' ? null : parseInt(raw, 10);
              onPlatesChange(Number.isInteger(n) && n >= 1 ? n : null);
            }}
          />
          {errFor('platesOrdered') && <div className="bq-field-error">{errFor('platesOrdered')}</div>}
        </div>

        <div className="tsr-manual-form-group">
          <label className="tsr-manual-label" htmlFor="bq-rate">Rate per plate</label>
          <input
            id="bq-rate"
            type="number"
            className="tsr-manual-input"
            placeholder="0.00"
            min="0"
            step="0.01"
            value={ratePerPlate ?? ''}
            disabled={disabled}
            onChange={(e) => onFieldChange('ratePerPlate', e.target.value === '' ? null : e.target.value)}
          />
          {errFor('ratePerPlate') && <div className="bq-field-error">{errFor('ratePerPlate')}</div>}
        </div>
      </div>

      {/* Derived, never typed. Shown only after entering the fields above and before confirming */}
      {canConfirm && !isConfirmed && (
        <>
          <div className="bq-total-row">
            <span className="bq-total-label">Total bill</span>
            <span className="bq-total-value">
              {totalBill === null ? '—' : money(totalBill)}
            </span>
          </div>
          {totalBill !== null && (
            <div className="bq-total-formula">
              {platesOrdered} plates × ₹{Number(ratePerPlate).toLocaleString('en-IN')}
            </div>
          )}

          {/* Says what the plate change did, so a kept override is visibly kept
              rather than silently ignored. */}
          {lastCascade && (lastCascade.updated > 0 || lastCascade.kept > 0) && (
            <div className="bq-cascade-note" role="status">
              Updated {lastCascade.updated} quantit{lastCascade.updated === 1 ? 'y' : 'ies'}
              {lastCascade.kept > 0 && ` · ${lastCascade.kept} override${lastCascade.kept === 1 ? '' : 's'} kept`}
            </div>
          )}

          <button
            type="button"
            className="bq-confirm-btn"
            disabled={disabled}
            onClick={onConfirm}
          >
            Confirm
          </button>
        </>
      )}
    </div>
  );
};

export default BanquetEntryFields;
